import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Icons } from "../ui/icons";
import BottomSheet from "./bottom-sheet";
import { useUser } from "../../hooks/use-user";
import { cn } from "../../lib/utils";
import {
  ACCOUNT_NAV_ITEMS,
  MAIN_NAV_ITEMS,
  NavItemConfig,
  canSeeNavItem,
  getUserInitials,
  isNavItemActive,
} from "../../lib/navigation";
import { MOBILE_TAB_ITEMS } from "./mobile-bottom-nav";

interface MobileMoreSheetProps {
  open: boolean;
  onClose: () => void;
}

const formatRole = (role?: string | null) =>
  role ? role.charAt(0) + role.slice(1).toLowerCase() : "";

const MenuTile: React.FC<{ item: NavItemConfig; isActive: boolean }> = ({ item, isActive }) => (
  <Link
    to={item.href}
    className="flex flex-col items-center gap-2 rounded-2xl p-2 text-center transition-transform active:scale-95 [-webkit-tap-highlight-color:transparent]"
  >
    <span
      className={cn(
        "flex h-12 w-12 items-center justify-center rounded-2xl",
        isActive ? "bg-primary-600 text-white" : "bg-primary-50 text-primary-600",
      )}
    >
      <item.icon width={22} height={22} />
    </span>
    <span
      className={cn(
        "text-xs leading-tight line-clamp-2",
        isActive ? "font-semibold text-gray-900" : "font-medium text-gray-700",
      )}
    >
      {item.label}
    </span>
  </Link>
);

const MobileMoreSheet: React.FC<MobileMoreSheetProps> = ({ open, onClose }) => {
  const { user, logout } = useUser();
  const { pathname } = useLocation();

  const tabHrefs = new Set(MOBILE_TAB_ITEMS.map((item) => item.href));
  const menuItems = MAIN_NAV_ITEMS.filter(
    (item) => !tabHrefs.has(item.href) && canSeeNavItem(item, user?.role),
  );
  const accountItems = ACCOUNT_NAV_ITEMS.filter((item) => canSeeNavItem(item, user?.role));

  return (
    <BottomSheet open={open} onClose={onClose} title="Menu">
      <div className="px-5 pb-5">
        {user && (
          <div className="mb-5 flex items-center gap-3 rounded-2xl bg-gray-50 p-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-sm font-semibold text-white">
              {getUserInitials(user.fullName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">{user.fullName}</p>
              <p className="truncate text-xs text-gray-500">{user.email}</p>
            </div>
            {user.role && (
              <span className="shrink-0 rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-semibold text-primary-700">
                {formatRole(user.role)}
              </span>
            )}
          </div>
        )}

        <div className="grid grid-cols-4 gap-x-1 gap-y-3">
          {menuItems.map((item) => (
            <MenuTile key={item.href} item={item} isActive={isNavItemActive(item, pathname)} />
          ))}
        </div>

        {accountItems.length > 0 && (
          <div className="mt-6">
            <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              More tools
            </p>
            <div className="overflow-hidden rounded-2xl border border-gray-100">
              {accountItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex items-center gap-3 border-b border-gray-100 px-4 py-3.5 last:border-b-0 active:bg-gray-50"
                >
                  <item.icon width={20} height={20} className="text-gray-500" />
                  <span className="flex-1 text-sm font-medium text-gray-800">{item.label}</span>
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </Link>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={logout}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-4 py-3.5 text-sm font-semibold text-red-600 active:bg-red-100"
        >
          <Icons.Logout className="h-4 w-4" />
          Log out
        </button>
      </div>
    </BottomSheet>
  );
};

export default MobileMoreSheet;
